import { type ReactNode, useState } from 'react';

import { Drawer } from '../../../components/Drawer';
import { HelpDrawerLink } from '../../../components/HelpDrawerLink';

interface HelpItem {
  body: ReactNode;
  heading: string;
}

interface ApprovalDetailsHelpProps {
  isForeignNationalApplication: boolean;
  isForeignNationalApprover: boolean;
}

const PERSONAL_DETAILS_HELP: HelpItem[] = [
  { body: 'This must be their legal first name as it is shown on official documents.', heading: 'Legal First Name' },
  {
    body: 'This must be their legal middle name as it is shown on official documents.',
    heading: 'Legal Middle Name',
  },
  { body: 'This must be their legal last name as it is shown on official documents.', heading: 'Legal Last Name' },
  {
    body: 'If the applicant has indicated they have a suffix (Jr., Sr., II, III, IV) to their name.',
    heading: 'Suffix',
  },
  {
    body: (
      <>
        <p>If the applicant has listed any names other than their current legal name that they have been known by.</p>
        <p>These may include:</p>
        <ul>
          <li>Maiden names</li>
          <li>Aliases</li>
          <li>Professional names</li>
          <li>Religious names</li>
          <li>Nicknames used on official documents</li>
          <li>Names from prior marriages</li>
        </ul>
      </>
    ),
    heading: 'Other Names Used',
  },
  {
    body: "The applicant's date of birth as it is shown on official documents. The applicant must be at least 18 years old.",
    heading: 'Date of Birth',
  },
  {
    body: 'The Social Security number will be shown as asterisks. This data is kept private.',
    heading: 'Social Security Number',
  },
  { body: "The applicant's home street address.", heading: 'Personal Street Address Line 1' },
  {
    body: "The applicant's home suite, floor, or building number (if this applies).",
    heading: 'Personal Street Address Line 2',
  },
  { body: "The city where the applicant's home is.", heading: 'Personal Address City' },
  { body: "The applicant's home state.", heading: 'Personal Address State' },
  { body: "The applicant's home 5-digit ZIP code.", heading: 'Personal Address Zip Code' },
  {
    body: 'The applicant has entered whether they will be working from their home address. This helps CMS to understand how they will work and may also affect certain benefits or requirements.',
    heading: 'Do you work from home?',
  },
  {
    body: "This should be the email address that the applicant's invitation was sent to. This is the primary email address that CMS will use to communicate with the applicant about their CMS user ID application. This should be an active email address that they check regularly.",
    heading: 'Registration Email',
  },
  {
    body: "The applicant's personal phone number. This number will be used to contact the applicant if CMS needs to verify information or if there are any issues with their application. This should be a number where the applicant can be reliably reached during business hours (9 AM - 5 PM ET).",
    heading: 'Personal Phone Number',
  },
  {
    body: (
      <>
        <p>
          The user category defines the type of employee the applicant is. This determines the type of access they will
          be granted. These may include:
        </p>
        <ul>
          <li>
            <strong>Federal Employee:</strong> Direct employees of the federal government
          </li>
          <li>
            <strong>Contractor:</strong> Contract workers providing services to CMS
          </li>
          <li>
            <strong>State Employee:</strong> State government employees working with CMS programs
          </li>
          <li>
            <strong>Other:</strong> Other categories not listed above
          </li>
        </ul>
      </>
    ),
    heading: 'User Category',
  },
];

const CONTRACT_DETAILS_HELP: HelpItem[] = [
  {
    body: 'This is the number for the contract the applicant is associated with. If it is wrong, please contact your roster owner.',
    heading: 'Contract Number',
  },
  {
    body: 'This is the name of the company or organization the applicant works for. The roster owner provided this information. If it is wrong, please contact your roster owner.',
    heading: 'Company',
  },
  {
    body: "This is the applicant's job title listed in the contract. It helps CMS understand what they will do and ensures they get the right access and permissions. The roster owner provided this information. If it's wrong, please contact your roster owner.",
    heading: 'Labor Category',
  },
  {
    body: 'The CMS region or facility where the applicant will be working. This is required to ensure they are granted access to the correct systems and locations.',
    heading: 'CMS Region/Facility',
  },
  {
    body: "The applicant's official work email address. This should not be a personal email. This address will be used to contact the applicant about work matters and their CMS access.",
    heading: 'Work Email',
  },
  {
    body: "The applicant's work phone number. This should be a direct line or desk phone where they can be reached during business hours (9 AM - 5 PM ET).",
    heading: 'Work Phone Number',
  },
  {
    body: "The applicant's work phone number may require an extension to reach them directly. This is left blank if it does not apply.",
    heading: 'Work Extension',
  },
  { body: "The street address for the applicant's workplace.", heading: 'Work Address Line 1' },
  {
    body: "The suite, floor, or building number of the applicant's workplace (if this applies).",
    heading: 'Work Address Line 2',
  },
  { body: "The city that the applicant's workplace is located in.", heading: 'Work Address City' },
  { body: "The state that the applicant's workplace is located in.", heading: 'Work Address State' },
  { body: "The 5-digit ZIP code the applicant's workplace is located in.", heading: 'Work Address Zip Code' },
];

const CITIZENSHIP_DETAILS_HELP: HelpItem[] = [
  {
    body: (
      <>
        <p>
          "Yes" means the applicant is a U.S. citizen by birth or naturalization. "No" means the applicant is not a U.S.
          citizen. If the applicant selected "No", there will be additional data and documents related to their
          immigration status available for review.
        </p>
        <p>
          Note: All applicants must be authorized to work in the United States. Non-citizens must provide valid work
          authorization documents.
        </p>
      </>
    ),
    heading: 'Are you a United States (U.S.) Citizen?',
  },
  { body: "The applicant's sex as it appears on their birth certificate.", heading: 'Sex' },
  { body: 'The country where the applicant was born.', heading: 'Birth Country' },
  { body: 'The city where the applicant was born.', heading: 'Birth City' },
  {
    body: 'The state or province where the applicant was born. If the applicant was born in the United States, this should be the state name. If they were born outside the U.S., this should be the province or region name.',
    heading: 'Birth State/Province',
  },
  {
    body: '"Yes" means the applicant is a lawful permanent resident (green card holder). "No" means they are not a permanent resident but have other work authorization.',
    heading: 'Are you a Permanent Resident of the United States (U.S.)?',
  },
  {
    body: 'The country where the applicant holds citizenship. If they hold dual citizenship, this is their primary citizenship.',
    heading: 'Country of Citizenship',
  },
  {
    body: 'The total number of full years the applicant has been physically present in the United States over the past five years. These do not need to be consecutive years.',
    heading: 'Years in the U.S. of the Past Five (5) Years',
  },
  {
    body: 'The date the applicant first entered the United States as an immigrant. This may be the date on their immigration visa or the date they became a permanent resident.',
    heading: 'Immigration Date',
  },
  {
    body: (
      <>
        <p>The type of immigration document the applicant has:</p>
        <ul>
          <li>
            <strong>Alien Registration Number (A-Number):</strong> A unique 9-digit number assigned by the U.S.
            Citizenship and Immigration Services (USCIS) to non-citizens. It may be found on their green card, work
            permit, or other immigration documents. The format is as follows: 123-456-789.
          </li>
          <li>
            <strong>Visa Number:</strong> An 8-character number printed in red ink on the bottom right corner of their
            visa document. It consists of either 8 digits (for example, 12345678) or 1 letter followed by 7 digits (for
            example, A1234567). This is different from the control number at the top of the visa.
          </li>
          <li>
            <strong>Work Permit Number:</strong> The 13-character number on their Employment Authorization Document
            (EAD) card issued by the U.S. Citizenship and Immigration Services (USCIS). Format: 3 letters followed by 10
            digits (for example, EAC1234567890). Common service center codes include EAC, WAC, LIN, SRC, NBC, MSC, and
            IOE.
          </li>
        </ul>
      </>
    ),
    heading: 'Immigration Document Type',
  },
];

function documentUploadHelp(): HelpItem[] {
  return [
    {
      body: (
        <>
          <p>Please confirm all required documents have been included in the application.</p>
          <p>
            <strong>Required Documents:</strong>
          </p>
          <ul>
            <li>
              <strong>PIV Training Certificate:</strong> Only the signed Certificate of Completion. This is found on the
              last page of the training.
            </li>
            <li>
              <strong>Resume:</strong> the applicant's current resume or curriculum vitae (CV).
            </li>
            <li>
              <strong>Passport:</strong> This is only required if the applicant said they're not a United States
              citizen. This must include a clear copy of the photo page of their current valid passport. Ensure all text
              is legible. Entry- and exit-stamped pages are not required. The only exceptions are U.S. permanent
              residents and asylum seekers.
            </li>
            <li>
              <strong>Residency Document:</strong> This is only required if the applicant said they're not a United
              States citizen.
              <ul>
                <li>U.S. visa or U.S. Permanent Resident card.</li>
                <li>
                  If the applicant's card has expired, check that they have provided a current Form I-94, Form I-797A,
                  and/or Form I-20 that specifies how long the expired card is extended, in addition to their expired
                  visa or U.S. Permanent Resident card.
                </li>
                <li>Canadian and Mexican citizens may instead upload the TN (temporary non-immigrant) visa.</li>
                <li>
                  Employment authorization cards (EAD), Social Security cards, or driver’s licenses are not accepted.
                  The applicant may have uploaded more than one document.
                </li>
              </ul>
            </li>
            <li>
              <strong>Legal and Other Documents:</strong> Any other files the applicant needed for their application.
              This could include documents for name changes, court orders, or other legal papers. The applicant may have
              uploaded more than one document.
            </li>
          </ul>
          <p>
            <strong>File Requirements</strong> <br /> Maximum file size: 4 MB per file. Supported formats vary by
            document type and are shown in each section.
          </p>
        </>
      ),
      heading: '',
    },
  ];
}

const COR_ADDITIONAL_ACCESS_HELP: HelpItem[] = [
  {
    body: 'This was set by the Roster Owner, but you can change it using the dropdown if it is incorrect. This affects the level of security checks the applicant will require before starting work.',
    heading: 'Will the applicant be working on this Contract for six (6) months or less?',
  },
  {
    body: 'Applicants who need routine unescorted access to CMS facilities may need a PIV card. Please consult the Contracting Officer for the contract to obtain information on travel costs. If no PIV card is issued, the applicant must be escorted by their Federal Approver or a designee at all times.',
    heading:
      'Will the applicant require routine access to any CMS facility OR require a PIV card to log into any CMS systems or applications?',
  },
];

const FOREIGN_NATIONAL_HELP: HelpItem[] = [
  {
    body: 'Enter the name of the organization the applicant will be working for if cleared. This information will help the DSI team and Foreign National Management System (FNMS) to determine if the applicant can be hired.',
    heading: 'What organization within CMS will the employee be working for?',
  },
  {
    body: 'Enter the types of logical access the applicant will need if cleared. This information will help the DSI team and Foreign National Management System (FNMS) to determine if the applicant can be hired.',
    heading:
      'What types of logical access (identification, authentication, authorization, etc.) will the employee need?',
  },
  {
    body: 'Enter the types of information the applicant will have access to if cleared. This information will help the DSI team and Foreign National Management System (FNMS) to determine if the applicant can be hired.',
    heading: 'What information (PII, PHI, etc.) will the employee have access to?',
  },
  {
    body: 'Enter the information systems the applicant will have access to if cleared. This information will help the Division of Strategic Information (DSI) team and Foreign National Management System (FNMS) to determine if the applicant can be hired.',
    heading: 'What information systems will the employee have access to?',
  },
];

const FNMS_FOREIGN_NATIONAL_HELP: HelpItem[] = FOREIGN_NATIONAL_HELP.map(({ heading }) => ({
  body: 'This information was entered by the Federal Approver for the contract on which the applicant will be working. Use this information to decide if they qualify.',
  heading,
}));

function HelpItems({ items }: { items: HelpItem[] }) {
  return (
    <>
      {items.map((item) => (
        <div key={item.heading}>
          <h4 className='ds-u-margin-top--3'>{item.heading}</h4>
          {typeof item.body === 'string' ? <p>{item.body}</p> : item.body}
        </div>
      ))}
    </>
  );
}

function ApprovalDetailsHelp({ isForeignNationalApplication, isForeignNationalApprover }: ApprovalDetailsHelpProps) {
  const [isHelpOpen, setIsHelpOpen] = useState(false);
  const foreignNationalHelp = isForeignNationalApprover ? FNMS_FOREIGN_NATIONAL_HELP : FOREIGN_NATIONAL_HELP;

  return (
    <>
      <HelpDrawerLink
        className='approval-details-page__help-link'
        data-testid='approval-details-help-link'
        onClick={() => setIsHelpOpen(true)}
      />
      <Drawer
        data-testid='approval-details-help-drawer'
        heading='Help: Manage Approvals'
        isOpen={isHelpOpen}
        width='50%'
        onClose={() => setIsHelpOpen(false)}>
        <div className='ds-content ds-u-padding--3'>
          <h3 className='ds-u-margin-top--0'>Get Help Introduction</h3>
          <p>
            You must review all of the applicant&apos;s information from their application. If you see any incorrect
            data, click the corresponding Request Edit button and enter the reason. In the bottom section, you must
            answer a question or questions before you can complete an action.{' '}
          </p>
          <p>Click "Back to Manage Approvals" to return to the table without taking any action on this application.</p>
          {isForeignNationalApprover && <p>Click "Send to FNMS" to send to the Foreign National Management System.</p>}
          <p>
            Click "Return or Edits" if you have identified one or more items that need to be fixed by the applicant.
          </p>
          <p>Click "Clear All" to remove all requests for edit within the application.</p>
          <p>
            Click "Decline and Delete" to remove the application from your queue without granting any CMS access to the
            applicant.
          </p>
          <p>Click "Approve" to approve access to the applicant and to send the application onto the next approver.</p>

          <h3>My Personal Details</h3>
          <HelpItems items={PERSONAL_DETAILS_HELP} />
          <h3>Contract Details</h3>
          <HelpItems items={CONTRACT_DETAILS_HELP} />
          <h3>My Citizenship Details</h3>
          <HelpItems
            items={isForeignNationalApplication ? CITIZENSHIP_DETAILS_HELP : CITIZENSHIP_DETAILS_HELP.slice(0, 5)}
          />
          <h3>Document Upload</h3>
          <HelpItems items={documentUploadHelp()} />
          {!isForeignNationalApprover && (
            <>
              <h3>Additional Access Information</h3>
              <HelpItems items={COR_ADDITIONAL_ACCESS_HELP} />
            </>
          )}
          {isForeignNationalApplication && (
            <>
              <h3>Foreign National Information</h3>
              <HelpItems items={foreignNationalHelp} />
            </>
          )}
        </div>
      </Drawer>
    </>
  );
}

export default ApprovalDetailsHelp;
